import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Filter + search state for the RCM All tab (server-side via GET /all).
 */
export function useRcmAllListFilters() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [filterClassification, setFilterClassification] = useState('all');
  const [filterSoxCycle, setFilterSoxCycle] = useState('all');
  const [filterDomain, setFilterDomain] = useState('all');
  const [filterSystem, setFilterSystem] = useState('all');
  const [filterRiskCategory, setFilterRiskCategory] = useState('all');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const handleClassificationChange = useCallback((value) => {
    const next = value || 'all';
    setFilterClassification(next);
    if (next === 'business') {
      setFilterDomain('all');
      setFilterSystem('all');
    } else if (next === 'itgc') {
      setFilterSoxCycle('all');
    }
  }, []);

  const resetFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setFilterClassification('all');
    setFilterSoxCycle('all');
    setFilterDomain('all');
    setFilterSystem('all');
    setFilterRiskCategory('all');
  }, []);

  const hasActiveListCriteria = useMemo(
    () =>
      debouncedSearchQuery !== '' ||
      filterClassification !== 'all' ||
      filterSoxCycle !== 'all' ||
      filterDomain !== 'all' ||
      filterSystem !== 'all' ||
      filterRiskCategory !== 'all',
    [
      debouncedSearchQuery,
      filterClassification,
      filterSoxCycle,
      filterDomain,
      filterSystem,
      filterRiskCategory,
    ]
  );

  const apiFilterParams = useMemo(
    () => ({
      search: debouncedSearchQuery,
      classificationFilter: filterClassification,
      soxCycleId: filterSoxCycle,
      domainCode: filterDomain,
      systemId: filterSystem,
      riskCategory: filterRiskCategory,
    }),
    [
      debouncedSearchQuery,
      filterClassification,
      filterSoxCycle,
      filterDomain,
      filterSystem,
      filterRiskCategory,
    ]
  );

  const domainFilterEnabled = filterClassification === 'all' || filterClassification === 'itgc';
  const systemFilterEnabled = domainFilterEnabled;
  const soxCycleFilterEnabled = filterClassification === 'all' || filterClassification === 'business';
  const riskCategoryFilterEnabled = true; // applies to both Process and ITGC rows

  return {
    searchQuery,
    setSearchQuery,
    filterClassification,
    setFilterClassification: handleClassificationChange,
    filterSoxCycle,
    setFilterSoxCycle,
    filterDomain,
    setFilterDomain,
    filterSystem,
    setFilterSystem,
    filterRiskCategory,
    setFilterRiskCategory,
    resetFilters,
    apiFilterParams,
    hasActiveListCriteria,
    domainFilterEnabled,
    systemFilterEnabled,
    soxCycleFilterEnabled,
    riskCategoryFilterEnabled,
  };
}
